import { apiSlice, DASHBOARD_CACHE_SECONDS } from '../../store/api/apiSlice';

/** One component row from the dashboardsummary response. */
export interface DashboardComponent {
  component_id: number;
  acd_yr: number;
  /** Parsed JSON payload for the component (shape varies per component). */
  data: unknown;
}

export interface SchoolDashboardData {
  total_schools: number;
  total_students: number;
  total_boys: number;
  total_girls: number;
  /** Transgender student count (backend: total_trans = SUM(total_t)). */
  total_trans: number;
  total_teachers: number;

  // Management-wise student counts.
  total_govt_students: number;
  total_unaided_students: number;
  total_aided_students: number;
  total_partial_aided_students: number;
  total_private_students: number;
  total_central_students: number;
  total_other_management_students: number;

  // Social category student counts.
  total_oc_students: number;
  total_bc_students: number;
  total_mbc_students: number;
  total_dnc_students: number;
  total_sc_students: number;
  total_st_students: number;

  // Enrolment by grade.
  total_pre_kg_students: number;
  total_lkg_students: number;
  total_ukg_students: number;
  total_1_students: number;
  total_2_students: number;
  total_3_students: number;
  total_4_students: number;
  total_5_students: number;
  total_6_students: number;
  total_7_students: number;
  total_8_students: number;
  total_9_students: number;
  total_10_students: number;
  total_11_students: number;
  total_12_students: number;
  [key: string]: number;
}

/** Envelope returned by GET api/dashboard/dashboardsummary. */
export interface DashboardSummaryResponse {
  dataStatus: boolean;
  status: number;
  message: string;
  /** Map keyed by component name → its payload. */
  result: Record<string, DashboardComponent>;
}

export interface DashboardSummaryArgs {
  /** Academic year id filter (optional). */
  acd_yr?: number;
  /** Component id filter (optional). */
  component_id?: number;
  /** Optional geography scope (district / block / school level). */
  district_id?: number;
  block_id?: number;
  school_id?: number;
}

/** A numeric-or-string value as the schoolinfrasummary API returns them. */
type InfraNum = number | string;

/** One academic-year compliance row from schoolinfrasummary. */
export interface SchoolInfraSummaryYear {
  ac_yr_id: string;
  academic_year: string;
  expected: InfraNum;
  submitted: InfraNum;
  pending: InfraNum;
  compliance_pct: number;
}

/** One academic-year row from infraAcdWiseSummary. */
export interface InfraAcdWiseItem {
  ac_yr_id: string;
  academic_year: string;
  /** Schools that SUBMITTED an infrastructure entry for this academic year. */
  total: InfraNum;
}

/** `result` payload of api/dashboard/infraAcdWiseSummary. */
export interface InfraAcdWiseSummaryResult {
  level: string;
  filters?: {
    district_id?: InfraNum;
    block_id?: InfraNum;
    school_id?: InfraNum;
  };
  year_count: InfraNum;
  total_records: InfraNum;
  latest_ac_yr_id: InfraNum;
  items: InfraAcdWiseItem[];
}

/** Envelope returned by api/dashboard/infraAcdWiseSummary. */
export interface InfraAcdWiseSummaryResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: InfraAcdWiseSummaryResult;
}

/** One infrastructure-type row from the schoolinfrasummary `overview`. */
export interface SchoolInfraSummaryOverviewRow {
  type: string;
  key: string;
  name: string;
  available: InfraNum;
  required: InfraNum;
  shortfall: InfraNum;
  surplus: InfraNum;
  availability_pct: number;
  coverage_pct: number;
  good_pct: number;
}

/** `result` payload of api/dashboard/schoolinfrasummary (one infra type). */
export interface SchoolInfraSummaryResult {
  type: string;
  type_key: string;
  type_name: string;
  ac_yr_id: string;
  academic_year: string;
  month_name?: string;
  academic_years: SchoolInfraSummaryYear[];
  kpis: {
    data_entry_compliance_pct: number;
    availability_vs_norms_pct: number;
    good_condition_pct: number;
    shortfall_gap_pct: number;
  };
  counts: {
    schools_expected: InfraNum;
    schools_submitted: InfraNum;
    entry_pending: InfraNum;
    available_units: InfraNum;
    required_by_norms: InfraNum;
    shortfall: InfraNum;
    surplus: InfraNum;
  };
  data_entry: {
    submitted: InfraNum;
    pending: InfraNum;
    submitted_pct: number;
    pending_pct: number;
  };
  vs_norms: { met_pct: number; shortfall_pct: number };
  condition: {
    good: InfraNum;
    need_repair: InfraNum;
    to_be_demolished: InfraNum;
    good_pct: number;
    need_repair_pct: number;
    to_be_demolished_pct: number;
  };
  overview: SchoolInfraSummaryOverviewRow[];
}

/** Envelope returned by POST api/dashboard/schoolinfrasummary. */
export interface SchoolInfraSummaryResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: SchoolInfraSummaryResult;
}

/** Request body for schoolinfrasummary. Scoped by geography for state /
 *  district / block level; `type` picks the infrastructure type. */
export interface SchoolInfraSummaryArgs {
  /** Infrastructure type id (1 classrooms, 2 toilets, 3 urinals, ...). */
  type?: number;
  /** Academic year id. */
  acd_yr?: number;
  /** Month code (e.g. "APR"); optional. */
  month?: string;
  /** District id → district-level summary. */
  district_id?: number;
  /** Block id → block-level summary. */
  block_id?: number;
  /** School id used by schoolinfratypedistricts / infraAcdWiseSummary. */
  school_id?: number;
  /** UDISE used by schoolinfrasummary (its table has no numeric school_id). */
  udise_code?: string;
  /** Management ids (comma-separated string or single id). */
  manage_id?: string | number;
  /** Category ids (comma-separated string or single id). */
  cate_id?: string | number;
  /** School-type ids (comma-separated string or single id). */
  school_type_id?: string | number;
}

/** One district row from schoolinfratypedistricts (per infra type). */
export interface SchoolInfraTypeDistrictRow {
  district_id?: number | string;
  district_name?: string;
  block_id?: number | string;
  block_name?: string;
  school_id?: number | string;
  school_name?: string;
  udise_code?: number | string;
  name?: string;
  available?: number | string;
  required?: number | string;
  norms?: number | string;
  shortfall?: number | string;
  surplus?: number | string;
  availability_pct?: number | string;
  coverage_pct?: number | string;
  good_pct?: number | string;
  gap_pct?: number | string;
  /** Data-entry: all-rows roster count for this district/block. */
  schools_expected?: number | string;
  /** Data-entry: rows with academic_year set (submitted). */
  schools_submitted?: number | string;
  /** submitted / expected * 100. */
  compliance_pct?: number | string;
  /** shortfall / required * 100. */
  shortfall_pct?: number | string;
  [key: string]: unknown;
}

/** `result` payload of schoolinfratypedistricts (one infra type). */
export interface SchoolInfraTypeDistrictsResult {
  type: string;
  key: string;
  title: string;
  ac_yr_id?: string;
  level?: string;
  group_key?: string;
  item_count?: string | number;
  items: SchoolInfraTypeDistrictRow[];
}

/** Envelope returned by GET api/dashboard/schoolinfratypedistricts. */
export interface SchoolInfraTypeDistrictsResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: SchoolInfraTypeDistrictsResult;
}

/** One caution tile from schoolinfracautionsummary. */
export interface SchoolInfraCautionTile {
  type: string;
  key: string;
  title: string;
  description: string;
  schools: string | number;
}

/** `result` of schoolinfracautionsummary. */
export interface SchoolInfraCautionSummaryResult {
  ac_yr_id?: string;
  total_schools: string | number;
  tiles: SchoolInfraCautionTile[];
}

export interface SchoolInfraCautionSummaryResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: SchoolInfraCautionSummaryResult;
}

/** One flagged school from schoolinfracautionlist. */
export interface SchoolInfraCautionSchool {
  udise_code: string;
  school_name: string;
  district_id?: string | number;
  district_name?: string;
  block_id?: string | number;
  block_name?: string;
  manage_id?: string | number;
  students?: string | number;
  finding?: string;
  [key: string]: unknown;
}

/** `result` of schoolinfracautionlist (server-paginated). */
export interface SchoolInfraCautionListResult {
  type: string;
  key: string;
  title: string;
  description?: string;
  ac_yr_id?: string;
  page: string | number;
  page_size: string | number;
  total: string | number;
  total_pages: string | number;
  schools: SchoolInfraCautionSchool[];
}

export interface SchoolInfraCautionListResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: SchoolInfraCautionListResult;
}

/** Shared args for the infra caution endpoints. */
export interface SchoolInfraCautionArgs {
  ac_yr?: number;
  district_id?: number;
  block_id?: number;
  manage_id?: string | number;
  cate_id?: string | number;
  school_type_id?: string | number;
}

export interface SchoolInfraCautionListArgs extends SchoolInfraCautionArgs {
  type?: number;
  page?: number;
  page_size?: number;
}

/** One academic-year row from the commonmasters `academic_year` array. */
export interface AcademicYearMaster {
  id: number;
  name: string;
  start_date?: string;
  end_date?: string;
  status?: string | number;
  isactive?: string | number;
  sequence_id?: string | number;
}

/** Management-category master (commonmasters → manage_cate). */
export interface ManageCateMaster {
  id: number;
  manage_name: string;
  [key: string]: unknown;
}

/** Management master (commonmasters → management, when enabled). */
export interface ManagementMaster {
  id: number;
  management: string;
  management_shortname?: string;
  [key: string]: unknown;
}

/** School-category master (commonmasters → category, when enabled). */
export interface CategoryMaster {
  id: number;
  category_name: string;
  [key: string]: unknown;
}

/** Envelope returned by GET api/dashboard/commonmasters. */
export interface CommonMastersResponse {
  dataStatus: boolean;
  status: number;
  message: string;
  result: {
    academic_year?: AcademicYearMaster[];
    /** Management categories (always returned). */
    manage_cate?: ManageCateMaster[];
    /** Managements (only when the backend enables it). */
    management?: ManagementMaster[];
    /** School categories (only when the backend enables it). */
    category?: CategoryMaster[];
    /** School types (schoolnew_type → { id, category_name }). */
    school_type?: {
      id: number;
      category_name: string;
      [key: string]: unknown;
    }[];
    [key: string]: unknown;
  };
}

/** One district row from the alldistrictlist response. */
export interface DistrictMaster {
  id: number;
  district_code?: string | number;
  district_name: string;
  short_name?: string;
  district_name_tamil?: string;
  [key: string]: unknown;
}

/** Envelope returned by GET api/dashboard/alldistrictlist. */
export interface AllDistrictListResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: {
    schooldist?: DistrictMaster[];
    [key: string]: unknown;
  };
}

/** One block row from the allblocklist response. */
export interface BlockMaster {
  id: number;
  block_name: string;
  block_code?: string | number;
  district_id?: number;
  [key: string]: unknown;
}

/** Envelope returned by GET api/dashboard/allblocklist?district_id=<id>. */
export interface AllBlockListResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: {
    // The array key may vary; support the common shapes.
    schoolblock?: BlockMaster[];
    block?: BlockMaster[];
    blocks?: BlockMaster[];
    [key: string]: unknown;
  };
}

/** One school row from the schoollist response. */
export interface SchoolMaster {
  id: number;
  school_name: string;
  udise_code?: string | number;
  block_id?: number;
  [key: string]: unknown;
}

/** Envelope returned by GET api/dashboard/schoollist?acd_yr=<id>&block_id=<id>. */
export interface SchoolListResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: {
    // The array key may vary; support the common shapes.
    school?: SchoolMaster[];
    schools?: SchoolMaster[];
    schoollist?: SchoolMaster[];
    [key: string]: unknown;
  };
}

/** One district row from the schoolabstract response. */
export interface SchoolAbstractDistrict {
  district_id: number;
  district_name: string;
  schools: number;
  total_boys: number;
  total_girls: number;
  total_students: number;
  teachers: number;
  [key: string]: unknown;
}

/** Envelope returned by POST api/dashboard/schoolabstract. */
export interface SchoolAbstractResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: {
    /** Full state-level metric block (same keys as the School Dashboard data). */
    summary?: Record<string, number>;
    /** One row per district. */
    districts?: SchoolAbstractDistrict[];
    [key: string]: unknown;
  };
}

/** One year-wise trend row (values may be strings via Convstr). */
export interface SchoolTrendRow {
  acd_yr: number | string;
  total_schools?: number | string;
  total_boys?: number | string;
  total_girls?: number | string;
  total_students?: number | string;
  total_teachers?: number | string;
  [key: string]: unknown;
}

/** Envelope returned by POST api/dashboard/schooltrends. */
export interface SchoolTrendsResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: SchoolTrendRow[];
}

/**
 * One drill row from schooltrendsdrill: the entity id (district_id / block_id /
 * school_id depending on the scope) plus one key per academic-year id → student
 * total for that entity in that year. Values may be strings via Convstr, so the
 * frontend coerces them. The exact set of per-year keys matches the acd_yr ids
 * in commonmasters.academic_year.
 */
export type SchoolTrendsDrillRow = Record<string, number | string> & {
  district_id?: number | string;
  district_name?: string;
  block_id?: number | string;
  block_name?: string;
  school_id?: number | string;
  school_name?: string;
};

/** Envelope returned by POST api/dashboard/schooltrendsdrill. */
export interface SchoolTrendsDrillResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: SchoolTrendsDrillRow[];
}

/** Caution alert types (schoolalerts). Ints map 1:1 to the backend. */
export const ALERT_TYPES = {
  zero_enrolment: 1,
  zero_teachers: 2,
  under_10_students: 3,
  single_teacher: 4,
  ptr_over_60: 5,
} as const;

/** Shared geography + classification filters for the alert endpoints. */
export interface SchoolAlertsArgs {
  acd_yr: number;
  district_id?: number | string;
  block_id?: number | string;
  school_id?: number | string;
  manage_id?: number[];
  cate_id?: number[];
  school_type_id?: number[];
}

/**
 * Caution card counts (schoolalerts). `result` is tolerant: either an object
 * keyed by alert name/id → count, or an array of { alert_type, count } rows.
 * The frontend reads whichever shape comes back.
 */
export interface SchoolAlertsResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result:
    | Record<string, number | string>
    | { alert_type?: number | string; count?: number | string }[];
}

/**
 * One flagged-school row from schoolalertslist. Matches the backend's
 * ALERT_DETAIL_FIELDS: identity + classification + `total` (students) and
 * `teach_tot` (teachers). There is NO per-row `finding` — the frontend derives
 * it from the alert type. Values may be strings via Convstr.
 */
export interface SchoolAlertRow {
  district_id?: number | string;
  district_name?: string;
  block_id?: number | string;
  block_name?: string;
  manage_id?: number | string;
  management?: string;
  cate_id?: number | string;
  cate_type?: string;
  category?: string;
  catty_id?: number | string;
  school_type?: string;
  school_type_id?: number | string;
  udise_code?: number | string;
  school_name?: string;
  /** Total students on roll (boys + girls + transgender). */
  total?: number | string;
  /** Total teaching staff. */
  teach_tot?: number | string;
  [key: string]: unknown;
}

/** Envelope returned by POST api/dashboard/schoolalertslist. */
export interface SchoolAlertsListResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: SchoolAlertRow[];
}

/** One per-school child-count row (values may be strings via Convstr). */
export interface ChildCountRow {
  school_id?: number | string;
  school_name?: string;
  udise_code?: number | string;
  management?: string;
  category?: string;
  school_type?: string;
  cate_type?: string;
  teach_tot?: number | string;
  total_t?: number | string;
  total_b?: number | string;
  total_g?: number | string;
  [key: string]: unknown;
}

/** Envelope returned by POST api/dashboard/studentchildcount. */
export interface StudentChildCountResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: ChildCountRow[];
}
// ── Attendance dashboard endpoint args ──
// Param cases (same for every attendance endpoint):
//   no params                 → last 30 working days, state level
//   edate                     → that single day, state level
//   district_id               → last 30 days within the district
//   edate + district_id       → that single day within the district
//   block_id                  → last 30 days down to school level in the block
//   edate + block_id          → that single day down to school level in the block
// Optional cross-cutting filters that can accompany any period/date call.
interface AttendanceFilterArgs {
  acd_yr?: number | string;
  management_id?: number | string;
  school_type?: number | string;
  cate_id?: number | string;
  /**
   * Nightly data generation stamp (from `useDataVersion`). Including it in the
   * args is what lets the client cache for a full day safely: the RTK Query
   * cache key is derived from the args, so a new batch generation produces new
   * keys and the stale entries are never read again. It also rides along as a
   * query param so each generation has its own URL — which is what makes
   * browser / CDN caching of these responses possible.
   */
  v?: string;
}
// `flag` selects the period window: 1=3d, 2=5d, 3=7d, 4=15d, 5=30d.
// Exactly one of `flag` / `edate` is always sent; the rest are optional.
export interface AttendanceDistrictArgs extends AttendanceFilterArgs {
  edate?: string;
  flag?: number;
  district_id?: number | string;
  /** Block scope (trend endpoints) — takes precedence over district_id. */
  block_id?: number | string;
  /** School scope — the deepest level, takes precedence over block_id. */
  school_id?: number | string;
  /** Metric selector for metric-trend endpoint (student/compliance/teacher). */
  metric?: number | string;
  /** Caution check selector for the caution-list endpoint. */
  type?: number | string;
  /**
   * Comma-separated caution categories (e.g. '1,2,3,4'). Returns every
   * requested category in ONE response under `by_type`, so switching caution
   * tiles costs no requests. Takes precedence over `type` on the server.
   */
  types?: string;
}
export interface AttendanceBlockArgs extends AttendanceFilterArgs {
  edate?: string;
  flag?: number;
  block_id?: number | string;
}

// Comparison tab endpoint (attendancecomparison). comparisontab selects the
// mode: 'districts' (one value per district for a window) or 'days' (day-wise
// groups, grouped by district/block/school depending on the id filters).
export interface AttendanceComparisonArgs {
  comparisontab?: 'districts' | 'days';
  metric?: number | string;
  /**
   * Comma-separated metric list (e.g. '1,2,3,4'). Returns every requested
   * metric in ONE response under `by_metric`, computed from a single student
   * scan + single teacher scan. Use this instead of firing one request per
   * metric. Takes precedence over `metric` on the server.
   */
  metrics?: string;
  /** Nightly data generation stamp — see AttendanceFilterArgs.v. */
  v?: string;
  /** Single-day mode: compare that exact date. Mutually exclusive with flag. */
  edate?: string;
  flag?: number | string;
  district_id?: number | string;
  block_id?: number | string;
  edu_dist_id?: number | string;
  school_type_id?: number | string;
  manage_id?: number | string;
  catty_id?: number | string;
  cate_id?: number | string;
}

// Year / monthly comparison endpoints (attendancedistrictmonthly,
// attendanceyearcomparison, attendanceblockmonthly). `from_year`/`to_year`
// drive the monthly endpoints; `year_a`/`year_b` drive the year comparison.
// Optional district_id / block_id narrow the scope; month_from / month_to
// restrict the academic-month window.
export interface AttendanceYearArgs extends AttendanceFilterArgs {
  from_year?: number | string;
  to_year?: number | string;
  year_a?: number | string;
  year_b?: number | string;
  district_id?: number | string;
  block_id?: number | string;
  month_from?: number | string;
  month_to?: number | string;
  // The Between-Years callers send the classification filters using the SAME
  // backend param names as the attendancecomparison endpoint (manage_id /
  // school_type_id / cate_id), not the Numbers-tab names (management_id /
  // school_type). Declare them here so yearParams can pass them through.
  manage_id?: number | string;
  school_type_id?: number | string;
}

const isSet = (v: unknown) => v != null && v !== '';

// Management / school-type / category filters that can ride along with any call.
const filterParams = (a?: AttendanceFilterArgs) => ({
  ...(isSet(a?.acd_yr) ? { acd_yr: a!.acd_yr } : {}),
  ...(isSet(a?.management_id) ? { management_id: a!.management_id } : {}),
  ...(isSet(a?.school_type) ? { school_type: a!.school_type } : {}),
  ...(isSet(a?.cate_id) ? { cate_id: a!.cate_id } : {}),
  // Data generation stamp — gives every generation its own URL. The API
  // ignores unknown query params, so this is purely a cache discriminator.
  ...(a?.v ? { v: a.v } : {}),
});

// Only include params that are actually set (empty string / null are dropped).
//
// block_id was missing here even though AttendanceDistrictArgs declares it and
// every caller (periodArgs/trendParams in Attendance.tsx) sets it on the
// request. That silently dropped block_id from the querystring for every
// endpoint built on districtParams (attendancenumbers, attendancesummary,
// attendancebands, attendancebyschooltype, attendancebydistrict,
// attendanceacrossperiods) — so selecting a block only ever sent district_id,
// the backend kept returning the DISTRICT's block list (district_detail.blocks)
// instead of the selected BLOCK's school list (block_detail.schools), and the
// Numbers tab fell back to synthesised dummy school names.
const districtParams = (a?: AttendanceDistrictArgs) => ({
  ...(a?.edate ? { edate: a.edate } : {}),
  ...(a?.flag != null ? { flag: a.flag } : {}),
  ...(isSet(a?.district_id) ? { district_id: a!.district_id } : {}),
  ...(isSet(a?.block_id) ? { block_id: a!.block_id } : {}),
  ...(isSet(a?.school_id) ? { school_id: a!.school_id } : {}),
  ...(isSet(a?.metric) ? { metric: a!.metric } : {}),
  ...filterParams(a),
});
const blockParams = (a?: AttendanceBlockArgs) => ({
  ...(a?.edate ? { edate: a.edate } : {}),
  ...(a?.flag != null ? { flag: a.flag } : {}),
  ...(isSet(a?.block_id) ? { block_id: a!.block_id } : {}),
  ...filterParams(a),
});

// Year / monthly comparison params. Only sends the keys that are set.
const yearParams = (a?: AttendanceYearArgs) => ({
  ...(isSet(a?.from_year) ? { from_year: a!.from_year } : {}),
  ...(isSet(a?.to_year) ? { to_year: a!.to_year } : {}),
  ...(isSet(a?.year_a) ? { year_a: a!.year_a } : {}),
  ...(isSet(a?.year_b) ? { year_b: a!.year_b } : {}),
  ...(isSet(a?.district_id) ? { district_id: a!.district_id } : {}),
  ...(isSet(a?.block_id) ? { block_id: a!.block_id } : {}),
  ...(isSet(a?.month_from) ? { month_from: a!.month_from } : {}),
  ...(isSet(a?.month_to) ? { month_to: a!.month_to } : {}),
  // Classification filters. The Between-Years callers use the comparison-style
  // param names (manage_id / school_type_id / cate_id), so read those directly
  // rather than filterParams (which expects management_id / school_type and so
  // silently dropped management + school-type selections here).
  ...(isSet(a?.manage_id) ? { manage_id: a!.manage_id } : {}),
  ...(isSet(a?.school_type_id) ? { school_type_id: a!.school_type_id } : {}),
  ...(isSet(a?.cate_id) ? { cate_id: a!.cate_id } : {}),
  // Nightly data-generation stamp (cache discriminator, ignored by the API).
  ...(a?.v ? { v: a.v } : {}),
});

// Trend endpoints (daily / metric): scope by block when a block is selected
// (school level), otherwise by district. Only one of block_id / district_id
// is sent. `metric` rides along for the metric-trend endpoint.
const trendParams = (a?: AttendanceDistrictArgs) => ({
  ...(a?.edate ? { edate: a.edate } : {}),
  ...(a?.flag != null ? { flag: a.flag } : {}),
  ...(isSet(a?.metric) ? { metric: a!.metric } : {}),
  // `types` (multi) wins over `type` (single) when both are present.
  ...(a?.types ? { types: a.types } : {}),
  ...(isSet(a?.type) ? { type: a!.type } : {}),
  ...(isSet(a?.school_id)
    ? { school_id: a!.school_id }
    : isSet(a?.block_id)
      ? { block_id: a!.block_id }
      : isSet(a?.district_id)
        ? { district_id: a!.district_id }
        : {}),
  ...filterParams(a),
});

/** Academic caution tile counts (academiccompliancecards). */
export interface AcademicComplianceCards {
  zero_mark_entry_compliance?: number;
  mark_entry_under_25?: number;
  [key: string]: number | undefined;
}
export interface AcademicComplianceCardsResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: AcademicComplianceCards;
}

/** One flagged school from academiccompliancelist. */
export interface AcademicComplianceSchool {
  district_name?: string;
  block_name?: string;
  school_name?: string;
  udise_code?: string;
  management?: string;
  students?: string | number;
  entered?: string | number;
  finding?: string;
  [key: string]: unknown;
}
export interface AcademicComplianceListResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: AcademicComplianceSchool[];
}

/** Shared args for the academic compliance endpoints. */
export interface AcademicComplianceArgs {
  acd_yr?: number;
  flag?: number;
  /**
   * Comma-separated band list (e.g. '1,2,3,4'). Returns every requested band in
   * ONE response under `by_flag`, built from a single scan. Use this instead of
   * re-requesting the endpoint on each caution-tile click.
   */
  flags?: string;
  district_id?: number;
  block_id?: number;
  school_id?: number;
}
/** Multi-band compliance list: every requested band in one response. */
export interface AcademicComplianceListMultiResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: {
    flags: number[];
    by_flag: Record<string, AcademicComplianceSchool[]>;
  };
}
/** Subject × entity comparison (academicsubjectcomparison). */
export interface AcademicSubjectComparisonSubject {
  subject_id: string;
  subject_name: string;
}
export interface AcademicSubjectComparisonRow {
  district_id?: string | number;
  block_id?: string | number;
  school_id?: string | number;
  district_name?: string;
  name?: string;
  subjects: Record<string, number>;
  overall?: number;
  [key: string]: unknown;
}
export interface AcademicSubjectComparisonResult {
  acd_yr?: string;
  metric?: string;
  metric_key?: string;
  metric_title?: string;
  class_range?: string;
  level?: string;
  group_key?: string;
  subject_ids: string[];
  subjects: AcademicSubjectComparisonSubject[];
  rows: AcademicSubjectComparisonRow[];
}
export interface AcademicSubjectComparisonResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: AcademicSubjectComparisonResult;
}
export interface AcademicSubjectComparisonArgs {
  acd_yr?: number;
  metric?: number;
  district_id?: number;
  block_id?: number;
  school_id?: number;
  manage_id?: string | number;
  cate_id?: string | number;
  school_type_id?: string | number;
}
/** Numeric fields from the academic exam APIs can arrive as "0" strings. */
type NumLike = number | string;
/** One side (A or B) of an exam comparison (academicexamcompare). */
export interface AcademicExamSide {
  class_id: string;
  exam_type_id: string;
  average_mark: NumLike;
  pass_pct: NumLike;
  compliance: NumLike;
  entered: NumLike;
}
export interface AcademicExamCompareSubject {
  subject_id: string;
  subject_name: string;
  a_average_mark: NumLike;
  b_average_mark: NumLike;
  avg_mark_diff: NumLike;
  a_pass_pct: NumLike;
  b_pass_pct: NumLike;
  pass_pct_diff: NumLike;
}
export interface AcademicExamCompareResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: {
    acd_yr?: string;
    side_a: AcademicExamSide;
    side_b: AcademicExamSide;
    difference: {
      avg_mark_diff: NumLike;
      pass_pct_diff: NumLike;
      compliance_diff: NumLike;
    };
    subject_ids: string[];
    subjects: AcademicExamCompareSubject[];
  };
}
export interface AcademicExamCompareArgs {
  acd_yr: number;
  class_a: number;
  exam_a: number;
  class_b: number;
  exam_b: number;
  subject_id?: number | null;
  district_id?: number | null;
  block_id?: number | null;
  school_id?: number | null;
}
/** One exam sitting (exam type × class) from academicexamsittings. */
export interface AcademicExamSittingRow {
  exam_type_id: string;
  class_id: string;
  average_mark: NumLike;
  pass_pct: NumLike;
  compliance: NumLike;
  entered: NumLike;
}
export interface AcademicExamSittingsResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: {
    acd_yr?: string;
    sitting_count?: NumLike;
    rows: AcademicExamSittingRow[];
  };
}
export interface AcademicExamSittingsArgs {
  acd_yr: number;
  class_id?: number | null;
  exam_type_id?: number | null;
  district_id?: number | null;
  block_id?: number | null;
  school_id?: number | null;
}
/** One district (or block, when drilled) row from academicexamdistrictimprovement. */
export interface AcademicExamImprovementRow {
  district_id?: string;
  block_id?: string;
  name: string;
  a_average_mark: NumLike;
  b_average_mark: NumLike;
  gain: NumLike;
  [key: string]: unknown;
}
export interface AcademicExamImprovementResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: {
    acd_yr?: string;
    exam_a?: string;
    exam_b?: string;
    level?: string;
    group_key?: string;
    item_count?: NumLike;
    rows: AcademicExamImprovementRow[];
  };
}
export interface AcademicExamImprovementArgs {
  acd_yr: number;
  exam_a: number;
  exam_b: number;
  class_from: number;
  class_to: number;
  district_id?: number | null;
  block_id?: number | null;
  school_id?: number | null;
}
// ── Academic Scores: Numbers tab ──
/** Geography scope shared by the academic Numbers / Trend endpoints. */
export interface AcademicGeoArgs {
  district_id?: number | null;
  block_id?: number | null;
  school_id?: number | null;
}
export interface AcademicYearArgs extends AcademicGeoArgs {
  acd_yr: number;
}
/** Summary cards (academicsummary / academicsummarystream). */
export interface AcademicSummaryCards {
  average_mark: NumLike;
  pass_percentage: NumLike;
  compliance: NumLike;
  students_expected: NumLike;
  marks_entered: NumLike;
  below_35_pct: NumLike;
}
/** One subject row (detail table + radar + mark bands). */
export interface AcademicSummarySubject {
  subject_id: string;
  subject_name?: string;
  average_mark: NumLike;
  pass_pct: NumLike;
  below_35: NumLike;
  band_35_60: NumLike;
  band_61_80: NumLike;
  above_80: NumLike;
  absentees: NumLike;
}
export interface AcademicExamProgressionRow {
  exam_type_id: string;
  average_mark: NumLike;
  pass_pct: NumLike;
  compliance: NumLike;
}
export interface AcademicClassWiseRow {
  class_id: string;
  average_mark: NumLike;
  pass_pct: NumLike;
}
export interface AcademicSummaryResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: {
    cards: AcademicSummaryCards;
    subjects: AcademicSummarySubject[];
    /** Class 6-10 endpoint only ('' when there is no data). */
    strongest_subject?: AcademicSummarySubject | '';
    weakest_subject?: AcademicSummarySubject | '';
    exam_progression?: AcademicExamProgressionRow[];
    class_wise?: AcademicClassWiseRow[];
  };
}
/** academicexamclasswise: exam progression (11-12) + class-wise (6-12). */
export interface AcademicExamClasswiseResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: {
    exam_progression: AcademicExamProgressionRow[];
    class_wise: AcademicClassWiseRow[];
  };
}
/** academicstreamcomparison: one row per Class 11-12 stream (group). */
export interface AcademicStreamComparisonResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: {
    stream_comparison: {
      stream_id: string;
      stream_name: string;
      average_mark: NumLike;
      pass_pct: NumLike;
    }[];
  };
}
/** One drill-down row (academicdistricts / academicdistrictsstream). */
export interface AcademicDistrictRow {
  district_id?: string;
  block_id?: string;
  school_id?: string;
  name: string;
  avg_mark: NumLike;
  pass_pct: NumLike;
  compliance: NumLike;
  weakest_subject_id?: string;
  weakest_subject_name?: string;
  weakest_subject_avg?: NumLike;
}
export interface AcademicDistrictsResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: {
    level: 'districts' | 'blocks' | 'schools';
    rows: AcademicDistrictRow[];
  };
}

// ── Academic Scores: Trend tab ──
export interface AcademicTrendArgs extends AcademicGeoArgs {
  from_year?: number | null;
  to_year?: number | null;
  /** Single id or comma-separated list. */
  class_id?: number | string | null;
  exam_type_id?: number | string | null;
}
export interface AcademicTrendPoint {
  ac_year: string;
  average_mark: NumLike;
  pass_pct: NumLike;
  compliance: NumLike;
}
export interface AcademicTrendResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: {
    by_year: AcademicTrendPoint[];
    by_year_exam_type: (AcademicTrendPoint & { exam_type_id: string })[];
  };
}
export interface AcademicTrendDistrictRow {
  district_id?: string;
  block_id?: string;
  school_id?: string;
  name: string;
  yearly: AcademicTrendPoint[];
}
export interface AcademicTrendDistrictResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: {
    level: 'districts' | 'blocks' | 'schools';
    rows: AcademicTrendDistrictRow[];
  };
}
/**
 * Combined Trend Analysis payload (academictrendcombined) — the union of what
 * academictrend and academictrenddistrict used to return separately, built from
 * a single aggregation instead of three.
 */
export interface AcademicTrendCombinedResponse {
  dataStatus: boolean;
  status: number;
  message?: string;
  result: {
    by_year: AcademicTrendPoint[];
    level: 'districts' | 'blocks' | 'schools';
    rows: AcademicTrendDistrictRow[];
  };
}

// Drop null / empty values so only the set filters reach the query string.
const academicParams = (a?: object) =>
  Object.fromEntries(
    Object.entries(a ?? {}).filter(([, v]) => v != null && v !== '')
  );

// Optional geography + classification filters shared by the Review Dashboard
// endpoints. Classification values are comma-separated id lists (multi-select).
export interface ReviewFilterArgs {
  district_id?: number | string;
  block_id?: number | string;
  manage_id?: number | string;
  cate_id?: number | string;
  school_type_id?: number | string;
}

const reviewParams = (a?: ReviewFilterArgs) => ({
  ...(isSet(a?.district_id) ? { district_id: a!.district_id } : {}),
  ...(isSet(a?.block_id) ? { block_id: a!.block_id } : {}),
  ...(isSet(a?.manage_id) ? { manage_id: a!.manage_id } : {}),
  ...(isSet(a?.cate_id) ? { cate_id: a!.cate_id } : {}),
  ...(isSet(a?.school_type_id) ? { school_type_id: a!.school_type_id } : {}),
});

export const tlmApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // State-level Review Dashboard payload (reviewdashboard). Returns the whole
    // state snapshot in one call; the Review Dashboard tab fires it on open.
    // Optional district/classification filters narrow it.
    getReviewDashboard: builder.query<any, ReviewFilterArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/reviewdashboard',
        method: 'GET',
        params: reviewParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
    }),

    // Enrollment detail table for the Review Dashboard drill (reviewenrollmenttable).
    getReviewEnrollmentTable: builder.query<any, ReviewFilterArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/reviewenrollmenttable',
        method: 'GET',
        params: reviewParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
    }),

    // Attendance detail table for the Review Dashboard drill (reviewattendancetable).
    getReviewAttendanceTable: builder.query<any, ReviewFilterArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/reviewattendancetable',
        method: 'GET',
        params: reviewParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
    }),

    // Infrastructure detail table for the Review Dashboard drill (reviewinfratable).
    getReviewInfraTable: builder.query<any, ReviewFilterArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/reviewinfratable',
        method: 'GET',
        params: reviewParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
    }),

    // Scholarship detail table for the Review Dashboard drill (reviewscholarshiptable).
    getReviewScholarshipTable: builder.query<any, ReviewFilterArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/reviewscholarshiptable',
        method: 'GET',
        params: reviewParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
    }),

    // Scholarship + THIRAN card KPIs for the Review Dashboard (reviewdashboard1).
    // Split out of reviewdashboard because that combined payload was slow;
    // this is fetched separately with the same geography + classification
    // filters, including district_id / block_id when the header has a scope.
    getReviewDashboard1: builder.query<any, ReviewFilterArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/reviewdashboard1',
        method: 'GET',
        params: reviewParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
    }),

    // THIRAN detail table for the Review Dashboard drill (reviewthirantable).
    getReviewThiranTable: builder.query<any, ReviewFilterArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/reviewthirantable',
        method: 'GET',
        params: reviewParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
    }),

    // SMC detail table for the Review Dashboard drill (reviewsmctable).
    getReviewSmcTable: builder.query<any, ReviewFilterArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/reviewsmctable',
        method: 'GET',
        params: reviewParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
    }),

    // Palli Paarvai detail panel for the Review Dashboard drill (pp_details).
    // view_id (1..6) selects the tab, scoped to the last completed month:
    //   1 class_observation, 2 schools_not_observed, 3 officials_not_observed,
    //   4 schools_3plus, 5 low_performers, 6 top_performers.
    // Standard review geography/classification filters apply (district_id /
    // block_id drive view 2's district->block->school drill).
    getPpDetails: builder.query<
      any,
      | (ReviewFilterArgs & {
          view_id?: number | string;
          /** Current academic year id (e.g. 37 → 2026-27). Review Dashboard
           * is frozen to the current year, so this is resolved once from the
           * header's year logic and sent on every call. */
          acd_yr?: number | string;
          /** Plain calendar month number (1-12, e.g. 11 for November) —
           * scopes views 1,3,4,5,6 to a specific month instead of the
           * backend's "last completed month" default. Comes from the Palli
           * Paarvai MONTH dropdown; acd_yr supplies the year context. */
          month_id?: number | string;
          /** 1/2/3 — groups 4 months each within the academic year (Term 1:
           * Jun-Sep, Term 2: Oct-Jan, Term 3: Feb-May). View 2 (Schools Not
           * Observed) uses this instead of month_id, from the TERM dropdown. */
          term_id?: number | string;
        })
      | void
    >({
      query: (args) => ({
        url: 'api/dashboard/pp_details',
        method: 'GET',
        params: {
          ...reviewParams(args ?? undefined),
          ...(isSet((args as any)?.view_id)
            ? { view_id: (args as any).view_id }
            : {}),
          ...(isSet((args as any)?.acd_yr)
            ? { acd_yr: (args as any).acd_yr }
            : {}),
          ...(isSet((args as any)?.month_id)
            ? { month_id: (args as any).month_id }
            : {}),
          ...(isSet((args as any)?.term_id)
            ? { term_id: (args as any).term_id }
            : {}),
        },
        service: 'djangoDashboard',
      }),
    }),

    // Palli Paarvai drill level 4: one official's observation detail list for
    // one month (date, udise_code, school_name, class, section) — reached by
    // clicking a user row in pp_details' officials table (views 1 & 3).
    // observed_by/acd_yr/month_id are all required by the backend.
    getPpUserObservations: builder.query<
      any,
      {
        observed_by: number | string;
        acd_yr: number | string;
        month_id: number | string;
      }
    >({
      query: (args) => ({
        url: 'api/dashboard/pp_user_observations',
        method: 'GET',
        params: {
          observed_by: args.observed_by,
          acd_yr: args.acd_yr,
          month_id: args.month_id,
        },
        service: 'djangoDashboard',
      }),
    }),

    // Academic Performance detail GRID for the Review Dashboard drill
    // (academicdetailtable). district_id / block_id / manage_id / cate_id /
    // school_type_id come from ReviewFilterArgs; exam_type_id (0-3: All/Q/H/A)
    // and class_range (0: 6-12, 1: 6-10, 2: 11-12) are specific to this grid.
    getAcademicDetailTable: builder.query<
      any,
      ReviewFilterArgs & {
        exam_type_id?: number | string;
        class_range?: number | string;
      }
    >({
      query: (args) => ({
        url: 'api/dashboard/academicdetailtable',
        method: 'GET',
        params: {
          ...reviewParams(args),
          ...(isSet(args?.exam_type_id)
            ? { exam_type_id: args.exam_type_id }
            : {}),
          ...(isSet(args?.class_range)
            ? { class_range: args.class_range }
            : {}),
        },
        service: 'djangoDashboard',
      }),
    }),

    // Academic Performance card detail for the Review Dashboard (academickpis).
    // Replaces the `academic` section of reviewdashboard, which was slow enough
    // to be dropped from that payload — this is fetched separately, same
    // geography + classification filters as the other review endpoints.
    getAcademicKpis: builder.query<any, ReviewFilterArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/academickpis',
        method: 'GET',
        params: reviewParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
    }),

    getDashboardSummary: builder.query<
      DashboardSummaryResponse,
      DashboardSummaryArgs | void
    >({
      query: (args) => ({
        url: 'api/dashboard/dashboardsummary',
        method: 'GET',
        params: {
          ...(args?.acd_yr != null && { acd_yr: args.acd_yr }),
          ...(args?.component_id != null && {
            component_id: args.component_id,
          }),
          ...(args?.district_id != null && { district_id: args.district_id }),
          ...(args?.block_id != null && { block_id: args.block_id }),
          ...(args?.school_id != null && { school_id: args.school_id }),
        },
        service: 'djangoDashboard',
      }),
    }),

    // Infrastructure summary for ONE type (schoolinfrasummary). Scoped by
    // geography: no id → state, district_id → district, block_id → block.
    getSchoolInfraSummary: builder.query<
      SchoolInfraSummaryResponse,
      SchoolInfraSummaryArgs | void
    >({
      query: (args) => ({
        url: 'api/dashboard/schoolinfrasummary',
        method: 'GET',
        params: {
          ...(args?.type != null && { type: args.type }),
          ...(args?.acd_yr != null && { acd_yr: args.acd_yr }),
          ...(args?.month != null && { month: args.month }),
          ...(args?.district_id != null && { district_id: args.district_id }),
          ...(args?.block_id != null && { block_id: args.block_id }),
          ...(args?.school_id != null && { school_id: args.school_id }),
          ...(args?.udise_code && { udise_code: args.udise_code }),
          ...(isSet(args?.manage_id) ? { manage_id: args!.manage_id } : {}),
          ...(isSet(args?.cate_id) ? { cate_id: args!.cate_id } : {}),
          ...(isSet(args?.school_type_id)
            ? { school_type_id: args!.school_type_id }
            : {}),
        },
        service: 'djangoDashboard',
      }),
    }),

    // Academic-year-wise infrastructure entry counts (infraAcdWiseSummary).
    // `items[].total` is the number of schools that SUBMITTED an entry in that
    // academic year. Expected / pending / compliance % are not returned — the
    // Infrastructure Numbers tab derives them against schools_expected from
    // schoolinfrasummary. Same geography/classification scoping as the rest.
    getInfraAcdWiseSummary: builder.query<
      InfraAcdWiseSummaryResponse,
      SchoolInfraSummaryArgs | void
    >({
      query: (args) => ({
        url: 'api/dashboard/infraAcdWiseSummary',
        method: 'GET',
        params: {
          ...(args?.type != null && { type: args.type }),
          ...(args?.acd_yr != null && { acd_yr: args.acd_yr }),
          ...(args?.month != null && { month: args.month }),
          ...(args?.district_id != null && { district_id: args.district_id }),
          ...(args?.block_id != null && { block_id: args.block_id }),
          ...(args?.school_id != null && { school_id: args.school_id }),
          ...(isSet(args?.manage_id) ? { manage_id: args!.manage_id } : {}),
          ...(isSet(args?.cate_id) ? { cate_id: args!.cate_id } : {}),
          ...(isSet(args?.school_type_id)
            ? { school_type_id: args!.school_type_id }
            : {}),
        },
        service: 'djangoDashboard',
      }),
    }),

    // Per-type district breakdown (schoolinfratypedistricts). Same request
    // shape as schoolinfrasummary; powers the "<type> by Districts" table.
    getSchoolInfraTypeDistricts: builder.query<
      SchoolInfraTypeDistrictsResponse,
      SchoolInfraSummaryArgs | void
    >({
      query: (args) => ({
        url: 'api/dashboard/schoolinfratypedistricts',
        method: 'GET',
        params: {
          ...(args?.type != null && { type: args.type }),
          ...(args?.acd_yr != null && { acd_yr: args.acd_yr }),
          ...(args?.month != null && { month: args.month }),
          ...(args?.district_id != null && { district_id: args.district_id }),
          ...(args?.block_id != null && { block_id: args.block_id }),
          ...(args?.school_id != null && { school_id: args.school_id }),
          ...(isSet(args?.manage_id) ? { manage_id: args!.manage_id } : {}),
          ...(isSet(args?.cate_id) ? { cate_id: args!.cate_id } : {}),
          ...(isSet(args?.school_type_id)
            ? { school_type_id: args!.school_type_id }
            : {}),
        },
        service: 'djangoDashboard',
      }),
    }),

    // Infra caution tiles (schoolinfracautionsummary): one tile per hard
    // check (zero classrooms / toilets / urinals / no water) with a count.
    getSchoolInfraCautionSummary: builder.query<
      SchoolInfraCautionSummaryResponse,
      SchoolInfraCautionArgs | void
    >({
      query: (args) => ({
        url: 'api/dashboard/schoolinfracautionsummary',
        method: 'GET',
        params: {
          ...(args?.ac_yr != null && { ac_yr: args.ac_yr }),
          ...(args?.district_id != null && { district_id: args.district_id }),
          ...(args?.block_id != null && { block_id: args.block_id }),
          ...(isSet(args?.manage_id) ? { manage_id: args!.manage_id } : {}),
          ...(isSet(args?.cate_id) ? { cate_id: args!.cate_id } : {}),
          ...(isSet(args?.school_type_id)
            ? { school_type_id: args!.school_type_id }
            : {}),
        },
        service: 'djangoDashboard',
      }),
    }),

    // Infra caution list (schoolinfracautionlist): server-paginated flagged
    // schools for one caution type.
    getSchoolInfraCautionList: builder.query<
      SchoolInfraCautionListResponse,
      SchoolInfraCautionListArgs | void
    >({
      query: (args) => ({
        url: 'api/dashboard/schoolinfracautionlist',
        method: 'GET',
        params: {
          ...(args?.type != null && { type: args.type }),
          ...(args?.ac_yr != null && { ac_yr: args.ac_yr }),
          ...(args?.page != null && { page: args.page }),
          ...(args?.page_size != null && { page_size: args.page_size }),
          ...(args?.district_id != null && { district_id: args.district_id }),
          ...(args?.block_id != null && { block_id: args.block_id }),
          ...(isSet(args?.manage_id) ? { manage_id: args!.manage_id } : {}),
          ...(isSet(args?.cate_id) ? { cate_id: args!.cate_id } : {}),
          ...(isSet(args?.school_type_id)
            ? { school_type_id: args!.school_type_id }
            : {}),
        },
        service: 'djangoDashboard',
      }),
    }),

    // Academic caution tiles (academiccompliancecards): mark-entry /
    // compliance counts per check.
    getAcademicComplianceCards: builder.query<
      AcademicComplianceCardsResponse,
      AcademicComplianceArgs | void
    >({
      query: (args) => ({
        url: 'api/dashboard/academiccompliancecards',
        method: 'GET',
        params: {
          ...(args?.acd_yr != null && { acd_yr: args.acd_yr }),
          ...(args?.district_id != null && { district_id: args.district_id }),
          ...(args?.block_id != null && { block_id: args.block_id }),
          ...(args?.school_id != null && { school_id: args.school_id }),
        },
        service: 'djangoDashboard',
      }),
    }),

    // Academic caution list (academiccompliancelist): flagged schools for one
    // check (flag). Full list, no pagination.
    // Every caution band in ONE call (flags=1,2,3,4) so switching tiles costs
    // no requests. The backend derives all bands from a single scan.
    getAcademicComplianceListMulti: builder.query<
      AcademicComplianceListMultiResponse,
      AcademicComplianceArgs
    >({
      query: (args) => ({
        url: 'api/dashboard/academiccompliancelist',
        method: 'GET',
        params: {
          ...(args?.acd_yr != null && { acd_yr: args.acd_yr }),
          ...(args?.flags && { flags: args.flags }),
          ...(args?.district_id != null && { district_id: args.district_id }),
          ...(args?.block_id != null && { block_id: args.block_id }),
          ...(args?.school_id != null && { school_id: args.school_id }),
        },
        service: 'djangoDashboard',
      }),
    }),
    getAcademicComplianceList: builder.query<
      AcademicComplianceListResponse,
      AcademicComplianceArgs | void
    >({
      query: (args) => ({
        url: 'api/dashboard/academiccompliancelist',
        method: 'GET',
        params: {
          ...(args?.acd_yr != null && { acd_yr: args.acd_yr }),
          ...(args?.flag != null && { flag: args.flag }),
          ...(args?.district_id != null && { district_id: args.district_id }),
          ...(args?.block_id != null && { block_id: args.block_id }),
          ...(args?.school_id != null && { school_id: args.school_id }),
        },
        service: 'djangoDashboard',
      }),
    }),
    // Subject × district/block/school comparison (academicsubjectcomparison):
    // one row per entity with a value per subject, for the chosen metric.
    getAcademicSubjectComparison: builder.query<
      AcademicSubjectComparisonResponse,
      AcademicSubjectComparisonArgs | void
    >({
      query: (args) => ({
        url: 'api/dashboard/academicsubjectcomparison',
        method: 'GET',
        params: {
          ...(args?.acd_yr != null && { acd_yr: args.acd_yr }),
          ...(args?.metric != null && { metric: args.metric }),
          ...(args?.district_id != null && { district_id: args.district_id }),
          ...(args?.block_id != null && { block_id: args.block_id }),
          ...(args?.school_id != null && { school_id: args.school_id }),
          ...(isSet(args?.manage_id) ? { manage_id: args!.manage_id } : {}),
          ...(isSet(args?.cate_id) ? { cate_id: args!.cate_id } : {}),
          ...(isSet(args?.school_type_id)
            ? { school_type_id: args!.school_type_id }
            : {}),
        },
        service: 'djangoDashboard',
      }),
    }),
    // Exam A (class + exam type) vs Exam B: side totals, the difference and
    // per-subject marks for the subjects both sittings share.
    getAcademicExamCompare: builder.query<
      AcademicExamCompareResponse,
      AcademicExamCompareArgs
    >({
      query: (body) => ({
        url: 'api/dashboard/academicexamcompare',
        method: 'POST',
        body,
        service: 'djangoDashboard',
      }),
    }),
    // Every exam sitting (exam type × class) for a year with its avg mark,
    // pass %, compliance and entered count.
    getAcademicExamSittings: builder.query<
      AcademicExamSittingsResponse,
      AcademicExamSittingsArgs
    >({
      query: (body) => ({
        url: 'api/dashboard/academicexamsittings',
        method: 'POST',
        body,
        service: 'djangoDashboard',
      }),
    }),
    // Per-district (per-block when district_id is set) avg mark in exam A vs
    // exam B over a class range, with the gain.
    getAcademicExamDistrictImprovement: builder.query<
      AcademicExamImprovementResponse,
      AcademicExamImprovementArgs
    >({
      query: (body) => ({
        url: 'api/dashboard/academicexamdistrictimprovement',
        method: 'POST',
        body,
        service: 'djangoDashboard',
      }),
    }),
    // ── Academic Scores: Numbers tab ──
    // Class 6-10 summary: cards, subjects, exam progression, class-wise.
    getAcademicSummary: builder.query<
      AcademicSummaryResponse,
      AcademicYearArgs
    >({
      query: (args) => ({
        url: 'api/dashboard/academicsummary',
        method: 'GET',
        params: academicParams(args),
        service: 'djangoDashboard',
      }),
    }),
    // Class 11-12 summary: cards + subjects.
    getAcademicSummaryStream: builder.query<
      AcademicSummaryResponse,
      AcademicYearArgs
    >({
      query: (args) => ({
        url: 'api/dashboard/academicsummarystream',
        method: 'GET',
        params: academicParams(args),
        service: 'djangoDashboard',
      }),
    }),
    // Exam progression (Class 11-12) + class-wise (Class 6-12).
    getAcademicExamClasswise: builder.query<
      AcademicExamClasswiseResponse,
      AcademicYearArgs
    >({
      query: (args) => ({
        url: 'api/dashboard/academicexamclasswise',
        method: 'GET',
        params: academicParams(args),
        service: 'djangoDashboard',
      }),
    }),
    // Class 11-12 stream-wise avg mark + pass %.
    getAcademicStreamComparison: builder.query<
      AcademicStreamComparisonResponse,
      AcademicYearArgs
    >({
      query: (args) => ({
        url: 'api/dashboard/academicstreamcomparison',
        method: 'GET',
        params: academicParams(args),
        service: 'djangoDashboard',
      }),
    }),
    // Drill-down summary table (districts → blocks → schools), Class 6-10.
    getAcademicDistricts: builder.query<
      AcademicDistrictsResponse,
      AcademicYearArgs
    >({
      query: (args) => ({
        url: 'api/dashboard/academicdistricts',
        method: 'GET',
        params: academicParams(args),
        service: 'djangoDashboard',
      }),
    }),
    // Drill-down summary table, Class 11-12.
    getAcademicDistrictsStream: builder.query<
      AcademicDistrictsResponse,
      AcademicYearArgs
    >({
      query: (args) => ({
        url: 'api/dashboard/academicdistrictsstream',
        method: 'GET',
        params: academicParams(args),
        service: 'djangoDashboard',
      }),
    }),

    // ── Academic Scores: Trend tab ──
    // Year-wise avg mark / pass % / compliance for the current scope.
    getAcademicTrend: builder.query<AcademicTrendResponse, AcademicTrendArgs>({
      query: (args) => ({
        url: 'api/dashboard/academictrend',
        method: 'GET',
        params: academicParams(args),
        service: 'djangoDashboard',
      }),
    }),
    // Per district (blocks / schools when drilled) year-wise series.
    getAcademicTrendDistrict: builder.query<
      AcademicTrendDistrictResponse,
      AcademicTrendArgs
    >({
      query: (args) => ({
        url: 'api/dashboard/academictrenddistrict',
        method: 'GET',
        params: academicParams(args),
        service: 'djangoDashboard',
      }),
    }),
    // Whole Trend Analysis tab in ONE call. Replaces getAcademicTrend +
    // getAcademicTrendDistrict for that tab: the backend derives the state-level
    // year series from the same per-entity aggregation instead of re-scanning
    // academic_scores_abstract for each shape.
    getAcademicTrendCombined: builder.query<
      AcademicTrendCombinedResponse,
      AcademicTrendArgs
    >({
      query: (args) => ({
        url: 'api/dashboard/academictrendcombined',
        method: 'GET',
        params: academicParams(args),
        service: 'djangoDashboard',
      }),
    }),

    // Static reference masters — includes the `academic_year` list used to
    // populate the header year dropdown (id = acd_yr, name = label).
    getCommonMasters: builder.query<CommonMastersResponse, void>({
      query: () => ({
        url: 'api/dashboard/commonmasters',
        method: 'GET',
        service: 'djangoDashboard',
      }),
    }),

    // All districts (Tamil Nadu) for the header district dropdown.
    getAllDistrictList: builder.query<AllDistrictListResponse, void>({
      query: () => ({
        url: 'api/dashboard/alldistrictlist',
        method: 'GET',
        service: 'djangoDashboard',
      }),
    }),

    // Blocks for a district — feeds the header block dropdown.
    getAllBlockList: builder.query<
      AllBlockListResponse,
      { district_id: number }
    >({
      query: ({ district_id }) => ({
        url: 'api/dashboard/allblocklist',
        method: 'GET',
        params: { district_id },
        service: 'djangoDashboard',
      }),
    }),

    // Schools for a block + academic year — feeds the header school dropdown.
    getSchoolList: builder.query<
      SchoolListResponse,
      {
        acd_yr: number;
        block_id: number;
        manage_id?: number | string;
        cate_id?: number | string;
        school_type_id?: number | string;
      }
    >({
      query: ({ acd_yr, block_id, manage_id, cate_id, school_type_id }) => ({
        url: 'api/dashboard/schoollist',
        method: 'GET',
        params: {
          acd_yr,
          block_id,
          ...(manage_id != null && manage_id !== '' ? { manage_id } : {}),
          ...(cate_id != null && cate_id !== '' ? { cate_id } : {}),
          ...(school_type_id != null && school_type_id !== ''
            ? { school_type_id }
            : {}),
        },
        service: 'djangoDashboard',
      }),
    }),

    // Multi-select scoped abstract (schoolabstractgeo accepts lists for
    // manage_id / cate_id / school_type_id). Returns a summary that already
    // SUMs across all selected ids — used by the Filter row's multi-select.
    getSchoolAbstractMulti: builder.query<
      SchoolAbstractResponse,
      {
        acd_yr: number;
        district_id?: number; // geography scope (combines with classification)
        manage_id?: string; // comma-separated ids
        cate_id?: string;
        school_type_id?: string;
      }
    >({
      query: (body) => ({
        url: 'api/dashboard/schoolabstractgeo',
        method: 'POST',
        body,
        service: 'djangoDashboard',
      }),
    }),

    // State-level abstract for a year, scoped by management / category /
    // school-type filters. Returns { summary, districts }. POST body.
    getSchoolAbstract: builder.query<
      SchoolAbstractResponse,
      {
        acd_yr: number;
        manage_id?: number;
        cate_id?: number;
        school_type_id?: number;
      }
    >({
      query: (body) => ({
        url: 'api/dashboard/schoolabstractallyear',
        method: 'POST',
        body,
        service: 'djangoDashboard',
      }),
    }),

    // Per-school child-count rows for a year, optionally scoped by district /
    // management / category / school-type. Carries full identity fields
    // (school_name, udise_code, management, category, school_type, teach_tot).
    getStudentChildCount: builder.query<
      StudentChildCountResponse,
      {
        acd_yr: number;
        district_id?: number;
        manage_id?: number;
        cate_id?: number;
        school_type_id?: number;
      }
    >({
      query: (body) => ({
        url: 'api/dashboard/studentchildcount',
        method: 'POST',
        body,
        service: 'djangoDashboard',
      }),
      // Keep each district's rows cached so drilling blocks → schools and
      // revisiting a district is instant (no refetch). Keyed by the args
      // (acd_yr + district_id). Uses the shared day-long lifetime — this is
      // nightly batch data, so there is nothing to refetch during the day.
      keepUnusedDataFor: DASHBOARD_CACHE_SECONDS,
    }),

    // Year-wise enrolment trend (schooltrends): one row per academic year
    // with total_schools/boys/girls/students/teachers. POST body with
    // optional geography + classification filters.
    getSchoolTrends: builder.query<
      SchoolTrendsResponse,
      {
        district_id?: number;
        block_id?: number;
        school_id?: number;
        manage_id?: string;
        cate_id?: string;
        school_type_id?: string;
      } | void
    >({
      query: (body) => ({
        url: 'api/dashboard/schooltrends',
        method: 'POST',
        body: body ?? {},
        service: 'djangoDashboard',
      }),
    }),

    // Year-wise drill (schooltrendsdrill): per-entity rows with per-year
    // student totals (keyed by acd_yr id). Districts when no filter, blocks
    // when a district_id is given, schools when a block_id is given. Powers the
    // top-N districts multi-line chart + the per-district change table.
    getSchoolTrendsDrill: builder.query<
      SchoolTrendsDrillResponse,
      {
        district_id?: number;
        block_id?: number;
        school_id?: number;
        manage_id?: string | number[];
        cate_id?: string | number[];
        school_type_id?: string | number[];
      } | void
    >({
      query: (body) => ({
        url: 'api/dashboard/schooltrendsdrill',
        method: 'POST',
        body: body ?? {},
        service: 'djangoDashboard',
      }),
    }),

    // Caution card COUNTS (schoolalerts): one count per alert type, scoped by
    // the academic year + optional geography/classification filters. Powers the
    // Caution tiles.
    getSchoolAlerts: builder.query<SchoolAlertsResponse, SchoolAlertsArgs>({
      query: (body) => ({
        url: 'api/dashboard/schoolalerts',
        method: 'POST',
        body,
        service: 'djangoDashboard',
      }),
    }),

    // Flagged-school LIST for one alert type (schoolalertslist): the detail
    // table under a selected caution tile. Same filters + a required
    // alert_type int (1=zero_enrolment … 5=ptr_over_60).
    getSchoolAlertsList: builder.query<
      SchoolAlertsListResponse,
      SchoolAlertsArgs & { alert_type: number | string }
    >({
      query: (body) => ({
        url: 'api/dashboard/schoolalertslist',
        method: 'POST',
        body,
        service: 'djangoDashboard',
      }),
    }),

    // Per-school rows for a district from the single all-years table (GET).
    // Powers the report drill-down: district → blocks → schools → detail.
    getStudentChildCountAllYear: builder.query<
      StudentChildCountResponse,
      { acd_yr: number; district_id: number }
    >({
      query: ({ acd_yr, district_id }) => ({
        url: 'api/dashboard/studentchildcountallyear',
        method: 'GET',
        params: { acd_yr, district_id },
        service: 'djangoDashboard',
      }),
      // Cache each district's rows so the block/school drill-down works off
      // cached data (no refetch per level). Shared day-long lifetime.
      keepUnusedDataFor: DASHBOARD_CACHE_SECONDS,
    }),
    // ── Data generation stamp ──────────────────────────────────
    // Identifies the current nightly batch generation of the dashboard data.
    // Every dashboard query carries this value in its args (see useDataVersion),
    // so cached entries are scoped to the generation they were built from: when
    // the batch lands a new version the cache keys change and the browser
    // refetches once, instead of either refetching constantly or risking stale
    // numbers. Deliberately short-lived in the cache so a long-open tab notices
    // the nightly rollover.
    getDataStamp: builder.query<{ version?: string }, void>({
      query: () => ({
        url: 'api/dashboard/datastamp',
        method: 'GET',
        service: 'djangoDashboard',
      }),
      transformResponse: (r: { result?: { version?: string } }) =>
        r?.result ?? {},
      keepUnusedDataFor: 300,
    }),
    // ── Attendance tab endpoints ───────────────────────────────
    // Combined "Numbers" sub-tab endpoint: one request returns every section
    // the Numbers tab renders (summary, bands, by_school_type, across_periods,
    // daily_trend) under result.{section}. Replaces the 6-8 separate calls that
    // previously fired on every filter change. State / district / block scoped
    // (edate? + flag? + district_id? + block_id? + classification filters).
    getAttendanceNumbers: builder.query<any, AttendanceDistrictArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/attendancenumbers',
        method: 'GET',
        params: districtParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
      transformResponse: (r: { result?: unknown }) => r?.result,
    }),
    // State / district scoped (edate? + district_id?).
    getAttendanceSummary: builder.query<any, AttendanceDistrictArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/attendancesummary',
        method: 'GET',
        params: districtParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
      transformResponse: (r: { result?: unknown }) => r?.result,
    }),
    getAttendanceBands: builder.query<any, AttendanceDistrictArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/attendancebands',
        method: 'GET',
        params: districtParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
      transformResponse: (r: { result?: unknown }) => r?.result,
    }),
    getAttendanceBySchoolType: builder.query<
      any,
      AttendanceDistrictArgs | void
    >({
      query: (args) => ({
        url: 'api/dashboard/attendancebyschooltype',
        method: 'GET',
        params: districtParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
      transformResponse: (r: { result?: unknown }) => r?.result,
    }),
    getAttendanceByDistrict: builder.query<any, AttendanceDistrictArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/attendancebydistrict',
        method: 'GET',
        params: districtParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
      transformResponse: (r: { result?: unknown }) => r?.result,
    }),
    getAttendanceAcrossPeriods: builder.query<
      any,
      AttendanceDistrictArgs | void
    >({
      query: (args) => ({
        url: 'api/dashboard/attendanceacrossperiods',
        method: 'GET',
        params: districtParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
      transformResponse: (r: { result?: unknown }) => r?.result,
    }),
    // Block / school scoped (edate? + block_id?).
    getAttendanceSchoolWise: builder.query<any, AttendanceBlockArgs>({
      query: (args) => ({
        url: 'api/dashboard/attendanceschoolwise',
        method: 'GET',
        params: blockParams(args),
        service: 'djangoDashboard',
      }),
      transformResponse: (r: { result?: unknown }) => r?.result,
    }),
    getAttendanceSchoolPeriods: builder.query<any, AttendanceBlockArgs>({
      query: (args) => ({
        url: 'api/dashboard/attendanceschoolperiods',
        method: 'GET',
        params: blockParams(args),
        service: 'djangoDashboard',
      }),
      transformResponse: (r: { result?: unknown }) => r?.result,
    }),
    // Daily attendance/compliance/teacher trend — loaded on the Trend Analysis
    // sub-tab. State / district scoped (edate? + district_id?).
    getAttendanceDailyTrend: builder.query<any, AttendanceDistrictArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/attendancedailytrend',
        method: 'GET',
        params: trendParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
      transformResponse: (r: { result?: unknown }) => r?.result,
    }),

    getAttendanceMetricTrend: builder.query<any, AttendanceDistrictArgs | void>(
      {
        query: (args) => ({
          url: 'api/dashboard/attendancemetrictrend',
          method: 'GET',
          params: trendParams(args ?? undefined),
          service: 'djangoDashboard',
        }),
        transformResponse: (r: { result?: unknown }) => r?.result,
      }
    ),

    getAttendanceCautionSummary: builder.query<
      any,
      AttendanceDistrictArgs | void
    >({
      query: (args) => ({
        url: 'api/dashboard/attendancecautionsummary',
        method: 'GET',
        params: trendParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
      transformResponse: (r: { result?: unknown }) => r?.result,
    }),

    getAttendanceCautionList: builder.query<any, AttendanceDistrictArgs | void>(
      {
        query: (args) => ({
          url: 'api/dashboard/attendancecautionlist',
          method: 'GET',
          params: trendParams(args ?? undefined),
          service: 'djangoDashboard',
        }),
        transformResponse: (r: { result?: unknown }) => r?.result,
      }
    ),

    // ── Year / monthly comparison endpoints (Comparison > Between Years) ──
    getAttendanceDistrictMonthly: builder.query<any, AttendanceYearArgs | void>(
      {
        query: (args) => ({
          url: 'api/dashboard/attendancedistrictmonthly',
          method: 'GET',
          params: yearParams(args ?? undefined),
          service: 'djangoDashboard',
        }),
        transformResponse: (r: { result?: unknown }) => r?.result,
      }
    ),

    getAttendanceYearComparison: builder.query<any, AttendanceYearArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/attendanceyearcomparison',
        method: 'GET',
        params: yearParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
      transformResponse: (r: { result?: unknown }) => r?.result,
    }),

    getAttendanceBlockMonthly: builder.query<any, AttendanceYearArgs | void>({
      query: (args) => ({
        url: 'api/dashboard/attendanceblockmonthly',
        method: 'GET',
        params: yearParams(args ?? undefined),
        service: 'djangoDashboard',
      }),
      transformResponse: (r: { result?: unknown }) => r?.result,
    }),

    // Comparison tab — attendancecomparison (districts | days modes).
    getAttendanceComparison: builder.query<
      any,
      AttendanceComparisonArgs | void
    >({
      query: (args) => ({
        url: 'api/dashboard/attendancecomparison',
        method: 'GET',
        params: {
          comparisontab: args?.comparisontab ?? 'districts',
          // `metrics` (multi) wins over `metric` (single) when both are given.
          ...(args?.metrics ? { metrics: args.metrics } : {}),
          ...(isSet(args?.metric) ? { metric: args!.metric } : {}),
          ...(args?.edate ? { edate: args.edate } : {}),
          ...(isSet(args?.flag) ? { flag: args!.flag } : {}),
          ...(isSet(args?.district_id)
            ? { district_id: args!.district_id }
            : {}),
          ...(isSet(args?.block_id) ? { block_id: args!.block_id } : {}),
          ...(isSet(args?.edu_dist_id)
            ? { edu_dist_id: args!.edu_dist_id }
            : {}),
          ...(isSet(args?.school_type_id)
            ? { school_type_id: args!.school_type_id }
            : {}),
          ...(isSet(args?.manage_id) ? { manage_id: args!.manage_id } : {}),
          ...(isSet(args?.catty_id) ? { catty_id: args!.catty_id } : {}),
          ...(isSet(args?.cate_id) ? { cate_id: args!.cate_id } : {}),
          // Data generation stamp — cache discriminator, ignored by the API.
          ...(args?.v ? { v: args.v } : {}),
        },
        service: 'djangoDashboard',
      }),
      transformResponse: (r: { result?: unknown }) => r?.result,
    }),
  }),
});

export const {
  useGetDashboardSummaryQuery,
  useGetSchoolInfraSummaryQuery,
  useGetInfraAcdWiseSummaryQuery,
  useGetSchoolInfraTypeDistrictsQuery,
  useGetSchoolInfraCautionSummaryQuery,
  useGetSchoolInfraCautionListQuery,
  useGetAcademicComplianceCardsQuery,
  useGetAcademicComplianceListQuery,
  useGetAcademicComplianceListMultiQuery,
  useGetAcademicSubjectComparisonQuery,
  useGetAcademicExamCompareQuery,
  useGetAcademicExamSittingsQuery,
  useGetAcademicExamDistrictImprovementQuery,
  useGetAcademicSummaryQuery,
  useGetAcademicSummaryStreamQuery,
  useGetAcademicExamClasswiseQuery,
  useGetAcademicStreamComparisonQuery,
  useGetAcademicDistrictsQuery,
  useGetAcademicDistrictsStreamQuery,
  useGetAcademicTrendQuery,
  useGetAcademicTrendDistrictQuery,
  useGetAcademicTrendCombinedQuery,
  useGetCommonMastersQuery,
  useGetAllDistrictListQuery,
  useGetAllBlockListQuery,
  useGetSchoolListQuery,
  useGetSchoolAbstractQuery,
  useGetSchoolAbstractMultiQuery,
  useGetStudentChildCountQuery,
  useGetStudentChildCountAllYearQuery,
  useGetSchoolTrendsQuery,
  useGetSchoolTrendsDrillQuery,
  useGetSchoolAlertsQuery,
  useGetSchoolAlertsListQuery,
  useLazyGetSchoolAlertsListQuery,
  useGetDataStampQuery,
  useGetReviewDashboardQuery,
  useGetReviewEnrollmentTableQuery,
  useGetReviewAttendanceTableQuery,
  useGetReviewInfraTableQuery,
  useGetReviewScholarshipTableQuery,
  useGetReviewDashboard1Query,
  useGetReviewThiranTableQuery,
  useGetReviewSmcTableQuery,
  useGetPpDetailsQuery,
  useLazyGetPpDetailsQuery,
  useGetPpUserObservationsQuery,
  useGetAcademicKpisQuery,
  useGetAcademicDetailTableQuery,
  useGetAttendanceNumbersQuery,
  useGetAttendanceSummaryQuery,
  useGetAttendanceBandsQuery,
  useGetAttendanceBySchoolTypeQuery,
  useGetAttendanceByDistrictQuery,
  useGetAttendanceAcrossPeriodsQuery,
  useGetAttendanceSchoolWiseQuery,
  useGetAttendanceSchoolPeriodsQuery,
  useGetAttendanceDailyTrendQuery,
  useGetAttendanceMetricTrendQuery,
  useGetAttendanceCautionSummaryQuery,
  useGetAttendanceCautionListQuery,
  useGetAttendanceDistrictMonthlyQuery,
  useGetAttendanceYearComparisonQuery,
  useGetAttendanceBlockMonthlyQuery,
  useGetAttendanceComparisonQuery,
  //   useSavePurchaseMutation,
  //   useGetPurchaseReportQuery,
} = tlmApi;
